import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SeedAccept, SeedTray } from '@travel-planner/shared';
import { api } from '../api/client';

export const trayKey = (tripId: string, countryId: string) =>
  ['seedTray', tripId, countryId] as const;

const base = (tripId: string, countryId: string) =>
  `/trips/${tripId}/countries/${countryId}/seed`;

export function useSeedTray(tripId: string, countryId: string) {
  return useQuery<SeedTray | null>({
    queryKey: trayKey(tripId, countryId),
    queryFn: () => api.get<SeedTray | null>(`${base(tripId, countryId)}/tray`),
  });
}

export function useGenerateSeed(tripId: string, countryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (interests?: string) =>
      api.post<SeedTray>(base(tripId, countryId), interests ? { interests } : {}),
    onSuccess: (tray) => qc.setQueryData(trayKey(tripId, countryId), tray),
  });
}

export function useAcceptStaged(tripId: string, countryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SeedAccept) =>
      api.post<{ tray: SeedTray }>(`${base(tripId, countryId)}/accept`, input),
    onSuccess: (res) => {
      qc.setQueryData(trayKey(tripId, countryId), res.tray);
      // The accepted suggestion becomes a list item (and a marker if located).
      qc.invalidateQueries({ queryKey: ['items', tripId] });
    },
  });
}

export function useDismissStaged(tripId: string, countryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SeedAccept) =>
      api.post<SeedTray>(`${base(tripId, countryId)}/dismiss`, input),
    onSuccess: (tray) => qc.setQueryData(trayKey(tripId, countryId), tray),
  });
}

export function useClearTray(tripId: string, countryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<void>(`${base(tripId, countryId)}/tray`),
    onSuccess: () => qc.setQueryData(trayKey(tripId, countryId), null),
  });
}
